import { env } from '@/env.generated';
import { authReady, getAccessToken, refreshAccessToken } from './supabase';

const BASE = env.API_BASE_URL;

export class ApiError extends Error {
	constructor(
		public status: number,
		message: string,
		public body?: unknown,
	) {
		super(message);
		this.name = 'ApiError';
	}
}

/**
 * Fetch wrapper for the Hono API: prepends VITE_API_BASE_URL and injects the
 * Supabase access token as `Authorization: Bearer`. Replaces the old
 * cookie-based `fetch('/api/...')` calls (Workers has no session cookies).
 *
 * `path` is the API path WITHOUT the leading `/api` (e.g. "/members").
 */
export async function api<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
	const headers = new Headers(init.headers);
	if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type'))
		headers.set('Content-Type', 'application/json');

	const send = (token: string | null) => {
		const h = new Headers(headers);
		if (token) h.set('Authorization', `Bearer ${token}`);
		return fetch(`${BASE}/api${path}`, { ...init, headers: h });
	};

	// ADR-006: synchronous token from the in-memory mirror (awaiting only the
	// one-time startup seed), one refresh+retry on 401.
	await authReady;
	let res = await send(getAccessToken());
	if (res.status === 401 && (init.body == null || typeof init.body === 'string')) {
		res = await send(await refreshAccessToken());
	}

	const contentType = res.headers.get('content-type') ?? '';
	const payload = contentType.includes('application/json')
		? await res.json().catch(() => null)
		: await res.text().catch(() => null);

	if (!res.ok) {
		const message =
			(payload && typeof payload === 'object' && 'error' in payload
				? String((payload as { error: unknown }).error)
				: typeof payload === 'string'
					? payload
					: res.statusText) || 'Request failed';
		throw new ApiError(res.status, message, payload);
	}
	return payload as T;
}

/** Returns the raw Response (for streaming endpoints like /chat). */
export async function apiRaw(path: string, init: RequestInit = {}): Promise<Response> {
	await authReady;
	const headers = new Headers(init.headers);
	const token = getAccessToken();
	if (token) headers.set('Authorization', `Bearer ${token}`);
	if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type'))
		headers.set('Content-Type', 'application/json');
	return fetch(`${BASE}/api${path}`, { ...init, headers });
}
